import { ErrorIcon } from "components/icons/icn-Error.icon";
import { HelpIcon } from "components/icons/icn-help.icon";
import { HelpIconDisabled } from "components/icons/icn-help-disable.icon";
import { searchSuggestionExplanation } from "pages/stocks-detail/stocks-detail.service";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import AppliedAdjustments from "./components/applied-adjustments";
import DeliveryDateDefinition from "./components/delivery-date-definition";
import HeaderContext from "./components/header-context";
import PickupDateDefinition from "./components/pickup-date-definition";
import ReplacementMaterialDecision, {
  type ReplacementMaterialDecisionDto,
} from "./components/replacement-material-decision";
import SimpleLine from "./components/simple-line";
import SupplierSelection from "./components/supplier-selection";
import UnconsideredInformation from "./components/unconsidered-information";
import WithTitleLine from "./components/with-title-line";
import { useOpenSuggestionPanel } from "./context/openSuggestionPanelContext";
import { BorderError, ButtonUnset, Content, ContentError, DescriptionText } from "./styled";

interface SuggestionAppliedAdjustmentsDto {
  QuantityCalculationFormula?: string;
  FinalQuantity?: number;
  AdjustmentExplanation?: string;
}

interface PickupDateDefinitionDto {
  DateOfTheNeed?: string;
  OriginalSuggestedPickupDate?: string;
  SupplierLeadtime?: string;
  AppliedAnticipationDaysOrHours?: string;
  Description?: string;
}

interface DeliveryDateDefinitionDto {
  OriginalSuggestedPickupDate?: string;
  OriginalSuggestedDeliveryDate?: string;
  TransitTime?: string;
  Description?: string;
}

interface SupplierSelectionDto {
  MainSupplier?: string;
  Criteria?: string;
  Description?: string;
}

interface SuggestionExplanationDto {
  SuggestionId: number;
  SuggestionCreationDate?: string;
  Reason?: string;
  QuantityCalculation?: string;
  AppliedAdjustments?: SuggestionAppliedAdjustmentsDto;
  PickupDateDefinition?: PickupDateDefinitionDto;
  DeliveryDateDefinition?: DeliveryDateDefinitionDto;
  SupplierSelection?: SupplierSelectionDto;
  ReplacementMaterialDecision?: ReplacementMaterialDecisionDto;
  Caveats?: string[];
}

type SuggestionContextOpenSide = "left" | "right";

interface SuggestionContextProps {
  suggestionId?: number;
  openSide?: SuggestionContextOpenSide;
  hasSnapshot?: boolean;
}

const OPEN_OFFSET_RIGHT = 15;
const OPEN_OFFSET_LEFT = 450;

const SuggestionContext = (props: SuggestionContextProps) => {
  const { suggestionId, openSide = "right", hasSnapshot } = props;

  const { openSuggestionId, toggleSuggestionPanel, closeSuggestionPanel } =
    useOpenSuggestionPanel();

  const isOpen = suggestionId != null && openSuggestionId === suggestionId;

  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const [position, setPosition] = useState({
    x: 100,
    y: 100,
  });

  const [explanation, setExplanation] = useState<SuggestionExplanationDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen || !suggestionId) return;

    let cancelled = false;
    setLoading(true);
    setError(false);

    searchSuggestionExplanation(suggestionId)
      .then((data: SuggestionExplanationDto) => {
        if (cancelled) return;
        setExplanation(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        setError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, suggestionId]);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest("button")) return;

    dragging.current = true;

    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };

    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragging.current) return;

    setPosition({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y,
    });
  }

  function handlePointerUp() {
    dragging.current = false;
  }

  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      closeSuggestionPanel();
    }
  }

  const hasNoExplanationData =
    !loading && !error && explanation && !explanation.Reason && !explanation.QuantityCalculation;

  return (
    <>
      {hasSnapshot ? (
        <ButtonUnset
          aria-label={intl.get("stocks.suggestionContext.title")}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (suggestionId == null) return;
            const x =
              openSide === "left" ? e.clientX - OPEN_OFFSET_LEFT : e.clientX + OPEN_OFFSET_RIGHT;
            setPosition({ x, y: e.clientY });
            toggleSuggestionPanel(suggestionId);
          }}
          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()}
        >
          <HelpIcon />
        </ButtonUnset>
      ) : (
        <HelpIconDisabled />
      )}

      {isOpen &&
        createPortal(
          <dialog
            open
            aria-modal="true"
            style={{
              position: "fixed",
              left: position.x,
              top: position.y,

              width: 400,
              maxHeight: "calc(100vh - 100px)",
              display: "flex",
              flexDirection: "column",

              boxSizing: "border-box",
              overflow: "hidden",

              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",

              margin: 0,
              padding: 0,
              background: "white",
              border: "1px solid #ccc",
              borderRadius: 12,
              userSelect: "none",
              zIndex: 9999,

              fontSize: "1rem",
              lineHeight: "normal",
              textAlign: "left",
              color: "#292929",
            }}
          >
            <div
              role="presentation"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
              onKeyDown={handlePanelKeyDown}
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              <div
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
              >
                <HeaderContext
                  suggestionCreationDate={explanation?.SuggestionCreationDate}
                  onClose={closeSuggestionPanel}
                />
              </div>
              <Content>
                {loading && (
                  <DescriptionText>{intl.get("stocks.suggestionContext.loading")}</DescriptionText>
                )}

                {!loading && (error || hasNoExplanationData) && (
                  <ContentError>
                    <BorderError>
                      <ErrorIcon color="#AE1E1E" />
                    </BorderError>
                    <DescriptionText style={{ whiteSpace: "pre-line" }}>
                      {intl.get("stocks.suggestionContext.unavailable")}
                    </DescriptionText>
                  </ContentError>
                )}

                {!loading && !error && explanation && (
                  <>
                    <SimpleLine reason={explanation.Reason} />
                    <WithTitleLine quantityCalculation={explanation.QuantityCalculation} />
                    <AppliedAdjustments
                      appliedAdjustments={
                        explanation.AppliedAdjustments && {
                          quantityCalculationFormula:
                            explanation.AppliedAdjustments.QuantityCalculationFormula,
                          finalQuantity: explanation.AppliedAdjustments.FinalQuantity,
                          adjustmentExplanation:
                            explanation.AppliedAdjustments.AdjustmentExplanation,
                        }
                      }
                    />
                    <PickupDateDefinition
                      dateOfTheNeed={explanation.PickupDateDefinition?.DateOfTheNeed}
                      appliedAnticipationDaysOrHours={
                        explanation.PickupDateDefinition?.AppliedAnticipationDaysOrHours
                      }
                      originalSuggestedPickupDate={
                        explanation.PickupDateDefinition?.OriginalSuggestedPickupDate
                      }
                      supplierLeadtime={explanation.PickupDateDefinition?.SupplierLeadtime}
                      Description={explanation.PickupDateDefinition?.Description}
                    />
                    <DeliveryDateDefinition
                      description={explanation.DeliveryDateDefinition?.Description}
                      originalSuggestedDeliveryDate={
                        explanation.DeliveryDateDefinition?.OriginalSuggestedDeliveryDate
                      }
                      originalSuggestedPickupDate={
                        explanation.DeliveryDateDefinition?.OriginalSuggestedPickupDate
                      }
                      transitTime={explanation.DeliveryDateDefinition?.TransitTime}
                    />
                    <SupplierSelection
                      criteria={explanation.SupplierSelection?.Criteria}
                      description={explanation.SupplierSelection?.Description}
                      mainSupplier={explanation.SupplierSelection?.MainSupplier}
                    />
                    <ReplacementMaterialDecision
                      replacementMaterialDecision={explanation.ReplacementMaterialDecision}
                    />
                    <UnconsideredInformation caveats={explanation.Caveats} />
                  </>
                )}
              </Content>
            </div>
          </dialog>,
          document.body
        )}
    </>
  );
};

export default SuggestionContext;
